import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import type { BarColumnCapabilities, BarColumnOrientation, BarColumnVariant } from '../../../chart-components/unified-bar-column-chart/unified-bar-column-chart.types';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import { isServerComputedClientTile } from '../../dvm-layout-builder/dvm-builder-client-chart.utils';
import type { BarBuilderMeasure, DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { BarVariantOption, FormulaBuilderConfig, LabelValueOption } from '../../shared/self-service-config.types';
import { SelfServiceFormulaService } from '../../shared/self-service-formula.service';
import { buildBarComputeWidgetData, extractBarComputeRows } from '../../shared/self-service-recompute.utils';
import type { ComputedColumn, FormulaTemplate } from '../../shared/self-service-types';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { applyBarFormatToCapabilities } from './bar-column-capabilities.utils';
import { applyComputedColumns } from './bar-computed-columns.utils';

/**
 * Bar & Column build form (BAR): ONE form for the whole family — orientation
 * toggle + variant (simple / grouped / stacked / 100% / clustered / …),
 * category (+ optional parent), series split, secondary axes, N measures
 * (draw-as / axis), reference lines, a backend value formula with per-group
 * overrides and typed references, the row-level Formula Builder (computed
 * columns), and the shared Format accordion. Renders client-side from the
 * cached dataset rows (live preview tile); Generate also POSTs, and a value
 * formula makes the backend rows replace the client ones.
 */
@Component({
  selector: 'app-bar-column-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './bar-column-step.component.html',
  styles: [':host { display: contents; }'],
})
export class BarColumnStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['BAR'] as const;
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);
  private readonly formulaService = inject(SelfServiceFormulaService);

  barOrientation: BarColumnOrientation = 'vertical';
  barVariant: BarColumnVariant = 'simple';
  barCategory = '';
  barCategoryParent = '';
  barCategoryLabel = '';
  barXAxisFontWeight = '';
  barYAxisFontWeight = '';
  barSecondaryAxisFontWeight = '';
  barSeries = '';

  barValueFormula = '';
  barValueLabel = '';
  barGroupFormulas: Array<{ group: string; formula: string }> = [];

  addBarGroupFormula(): void {
    this.barGroupFormulas = [...this.barGroupFormulas, { group: '', formula: '' }];
  }

  removeBarGroupFormula(entry: { group: string; formula: string }): void {
    this.barGroupFormulas = this.barGroupFormulas.filter((item) => item !== entry);
  }

  get barUsesValueFormula(): boolean {
    return !!this.barValueFormula.trim()
      || this.barGroupFormulas.some((entry) => entry.group.trim() && entry.formula.trim());
  }

  barReferenceLines: Array<{ id: string; value: number | null; label: string; color: string }> = [];
  private barRefLineIdCounter = 0;
  barMeasures: BarBuilderMeasure[] = [];
  barSecondaryXField = '';
  barSecondaryYField = '';
  private barMeasureIdCounter = 0;

  barDataAccordionOpen = true;

  barComputedColumns: ComputedColumn[] = [];
  newBarColumnName = '';
  newBarColumnExpression = '';
  newBarColumnError = '';
  barFormulaAccordionOpen = false;

  get barVariants(): BarVariantOption[] {
    return this.host.layout.options?.barVariants?.length
      ? this.host.layout.options.barVariants
      : [
          { label: 'Simple Bar', value: 'simple', usesSeries: false },
          { label: 'Grouped Bar', value: 'grouped', usesSeries: true },
          { label: 'Stacked Bar', value: 'stacked', usesSeries: true },
          { label: '100% Stacked Bar', value: 'stacked-100', usesSeries: true },
          { label: 'Clustered Bar Chart', value: 'clustered', usesSeries: true },
          { label: 'Comparison Bar (A vs B)', value: 'comparison', needsTwoMeasures: true },
        ];
  }

  private get barVariantEntry(): BarVariantOption | undefined {
    return this.barVariants.find((v) => v.value === this.barVariant);
  }
  get barRenderTypes(): LabelValueOption[] {
    return this.host.layout.options?.barRenderTypes?.length
      ? this.host.layout.options.barRenderTypes
      : [
          { label: 'Bar', value: 'bar' },
          { label: 'Line', value: 'line' },
          { label: 'Area', value: 'area' },
        ];
  }
  get barAxisOptions(): LabelValueOption[] {
    return this.host.layout.options?.barAxisOptions?.length
      ? this.host.layout.options.barAxisOptions
      : [
          { label: 'Primary axis', value: 'y1' },
          { label: 'Secondary axis (dual)', value: 'y2' },
        ];
  }
  get barVariantUsesSeries(): boolean {
    return this.barVariantEntry?.usesSeries === true;
  }

  get barVariantSeriesFromMeasures(): boolean {
    return this.barVariantEntry?.seriesFromMeasures === true;
  }

  get barSeriesComesFromMeasures(): boolean {
    return this.barVariantSeriesFromMeasures && !this.barSeries;
  }

  get barSeriesRequired(): boolean {
    return this.barVariantUsesSeries && !this.barVariantSeriesFromMeasures;
  }

  get barVariantHint(): string {
    return this.barVariantEntry?.hint || '';
  }

  get isBarComparison(): boolean {
    return this.barVariantEntry?.needsTwoMeasures === true;
  }

  get barAllowsMultipleMeasures(): boolean {
    if (this.barVariantEntry?.allowMultipleMeasures === true) return true;
    if (this.isBarComparison) return true;
    if (this.barSeriesComesFromMeasures) return true;
    return !this.barVariantUsesSeries;
  }

  private get axisLabelsConfig(): Record<string, string> {
    return (this.host.layout.options?.axisLabels ?? {}) as Record<string, string>;
  }
  get barCategoryAxisLabel(): string {
    const c = this.axisLabelsConfig;
    return this.barOrientation === 'horizontal'
      ? (c['categoryHorizontal'] || 'Y-axis (category)')
      : (c['categoryVertical'] || 'X-axis (category)');
  }
  get barValueAxisLabel(): string {
    const c = this.axisLabelsConfig;
    return this.barOrientation === 'horizontal'
      ? (c['valueHorizontal'] || 'X-axis (values)')
      : (c['valueVertical'] || 'Y-axis (values)');
  }
  get barSecondaryValueAxis(): 'x' | 'y' {
    return this.barOrientation === 'horizontal' ? 'x' : 'y';
  }
  isBarSecondaryAxisActive(which: 'x' | 'y'): boolean {
    return this.barSecondaryValueAxis === which;
  }
  get barActiveSecondaryField(): string {
    return this.barSecondaryValueAxis === 'x' ? this.barSecondaryXField : this.barSecondaryYField;
  }

  get barHasSecondaryAxis(): boolean {
    return !!this.barActiveSecondaryField.trim() || this.barMeasures.some((m) => !!m.field && m.axis === 'y2');
  }

  onBarValueFormulaFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.barValueFormula = this.host.appendFormulaToken(this.barValueFormula, mapping.field);
    this.onBarChange();
  }

  private barColumnsCache: string[] = [];
  private barColumnsKey = '';
  get barColumns(): string[] {
    const base = this.host.drillDatasetColumns;
    const computed = this.barComputedColumns.map((c) => c.name);
    const key = `${base.join('|')}::${computed.join('|')}`;
    if (key !== this.barColumnsKey) {
      this.barColumnsKey = key;
      this.barColumnsCache = [...base, ...computed.filter((n) => !base.includes(n))];
    }
    return this.barColumnsCache;
  }


  get formulaBuilderConfig(): FormulaBuilderConfig {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['formulaBuilder'] as FormulaBuilderConfig | undefined) ?? {};
  }
  get formulaBuilderEnabled(): boolean {
    return this.formulaBuilderConfig.enabled !== false;
  }
  get barFormulaTemplates(): FormulaTemplate[] {
    const list = Array.isArray(this.formulaBuilderConfig.templates) ? this.formulaBuilderConfig.templates : [];
    return list.filter((t) => t.kind === 'column');
  }

  applyBarFormulaTemplate(tpl: FormulaTemplate): void {
    this.newBarColumnName = tpl.defaultName;
    this.newBarColumnExpression = tpl.expressionTemplate;
    this.newBarColumnError = '';
  }

  addBarComputedColumn(): void {
    const name = this.newBarColumnName.trim();
    const expression = this.newBarColumnExpression.trim();
    this.newBarColumnError = '';
    const cfg = this.formulaBuilderConfig;
    if (!name) { this.newBarColumnError = 'Column name is required.'; return; }
    if (!expression) { this.newBarColumnError = 'Expression is required.'; return; }
    const pattern = cfg.namePattern ? new RegExp(cfg.namePattern) : /^[A-Za-z_][A-Za-z0-9_ ]*$/;
    if (!pattern.test(name)) {
      this.newBarColumnError = cfg.nameError || 'Invalid column name.';
      return;
    }
    if (this.host.drillDatasetColumns.includes(name) || this.barComputedColumns.some((c) => c.name === name)) {
      this.newBarColumnError = `A column named "${name}" already exists.`;
      return;
    }
    try {
      this.formulaService.buildRowEvaluator(expression, this.barColumns);
    } catch (e) {
      this.newBarColumnError = `Invalid expression: ${e instanceof Error ? e.message : 'parse error'}`;
      return;
    }
    this.barComputedColumns = [...this.barComputedColumns, { name, expression }];
    this.newBarColumnName = '';
    this.newBarColumnExpression = '';
    this.onBarChange();
  }

  removeBarComputedColumn(name: string): void {
    this.barComputedColumns = this.barComputedColumns.filter((c) => c.name !== name);
    if (this.barCategory === name) this.barCategory = '';
    if (this.barSeries === name) this.barSeries = '';
    this.barMeasures = this.barMeasures.map((m) => (m.field === name ? { ...m, field: '' } : m));
    this.onBarChange();
  }

  /** The dataset rows with this form's computed columns added (the live preview's data). */
  private applyBarComputedColumns(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    return applyComputedColumns(rows, this.barComputedColumns, this.host.drillDatasetColumns, (expression, names) => this.formulaService.buildRowEvaluator(expression, names));
  }

  addBarMeasure(): void {
    this.barMeasures = [
      ...this.barMeasures,
      { id: this.nextBarMeasureId(), field: '', label: '', renderAs: 'bar', axis: 'y1' },
    ];
  }

  removeBarMeasure(measure: BarBuilderMeasure): void {
    this.barMeasures = this.barMeasures.filter((item) => item.id !== measure.id);
    this.host.invalidatePreview();
  }

  moveBarMeasureUp(index: number): void {
    if (index <= 0) return;
    this.reorderBarMeasure(index, index - 1);
  }

  moveBarMeasureDown(index: number): void {
    if (index >= this.barMeasures.length - 1) return;
    this.reorderBarMeasure(index, index + 1);
  }

  private reorderBarMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.barMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.barMeasures = next;
    this.host.invalidatePreview();
  }

  onBarMeasureChange(): void {
    this.barMeasures = [...this.barMeasures];
    this.host.invalidatePreview();
    this.refreshEditingBarTile();
  }

  onBarChange(): void {
    this.host.invalidatePreview();
    this.refreshEditingBarTile();
  }

  refreshEditingBarTile(): void {
    if (!this.active) return;

    if (this.host.editingTileId) {
      const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
      const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'BAR') {
        this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? this.rebuildBarTile(tile) : t));
      }
      return;
    }

    if (!this.canPreviewBar) {
      this.host.removePreviewTile();
      return;
    }

    const existingIndex = this.host.previewTileId ? this.host.dashboardTiles.findIndex((t) => t.id === this.host.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.host.newTileBase(this.host.buildPayload());
      this.host.previewTileId = base.id;
      const draft = this.rebuildBarTile({ ...base, layoutType: 'BAR' });
      this.host.dashboardTiles = [...this.host.dashboardTiles, draft];
    } else {
      const tile = this.host.dashboardTiles[existingIndex];
      this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildBarTile(tile) : t));
    }
  }

  private rebuildBarTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.host.defaultTileFormat('BAR'), ...this.host.barFormat, title: this.host.title.trim() || undefined };
    const existing = tile.barData as Record<string, unknown> | null | undefined;
    if (existing && isServerComputedClientTile(existing, this.barCategory)) {
      const caps = this.applyBarFormatToCapabilities(
        { ...(existing['capabilities'] as BarColumnCapabilities) },
        format,
      );
      return {
        ...tile,
        title: this.host.title.trim() || tile.title || 'Untitled',
        format,
        barData: { ...existing, title: this.host.title.trim() || tile.title, capabilities: caps },
      };
    }
    const rows = this.applyBarComputedColumns(this.host.cachedRows(this.host.drillDataset) || []);
    return {
      ...tile,
      title: this.host.title.trim() || tile.title || 'Untitled',
      format,
      barRows: rows,
      barData: this.buildBarWidgetData(tile.id, format),
    };
  }

  onBarCategoryFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.barCategory = mapping.field;
    if (!this.barCategoryLabel) this.barCategoryLabel = mapping.field;
    this.host.invalidatePreview();
  }

  onBarSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.barSeries = mapping.field;
    this.host.invalidatePreview();
  }

  onBarParentFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.barCategoryParent = mapping.field;
    this.host.invalidatePreview();
  }

  addBarReferenceLine(): void {
    this.barRefLineIdCounter += 1;
    this.barReferenceLines = [
      ...this.barReferenceLines,
      { id: `bar-refline-${this.barRefLineIdCounter}`, value: null, label: '', color: '#64748b' },
    ];
  }

  removeBarReferenceLine(id: string): void {
    this.barReferenceLines = this.barReferenceLines.filter((line) => line.id !== id);
    this.host.invalidatePreview();
  }

  onBarReferenceLineChange(): void {
    this.barReferenceLines = [...this.barReferenceLines];
    this.host.invalidatePreview();
    this.refreshEditingBarTile();
  }

  onBarSecondaryAxisFieldDrop(event: DragEvent, which: 'x' | 'y'): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    if (which === 'x') this.barSecondaryXField = mapping.field;
    else this.barSecondaryYField = mapping.field;
    this.host.invalidatePreview();
  }

  onBarMeasureFieldDrop(event: DragEvent, measure?: BarBuilderMeasure): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    const target = measure || { id: this.nextBarMeasureId(), field: '', label: '', renderAs: 'bar' as const, axis: 'y1' as const };
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!measure) this.barMeasures = [...this.barMeasures, target];
    this.onBarMeasureChange();
  }

  barColumnPlaceholder(): string {
    if (!this.host.drillDataset) return 'Select a dataset first';
    if (this.host.isDrillDatasetColumnsLoading) return 'Loading columns...';
    return 'Select column';
  }

  private nextBarMeasureId(): string {
    this.barMeasureIdCounter += 1;
    return `bar-measure-${this.barMeasureIdCounter}`;
  }

  get canPreviewBar(): boolean {
    if (!this.active || !this.host.drillDataset) return false;
    if (!this.host.cachedRows(this.host.drillDataset)?.length) return false;
    const filledMeasures = this.barMeasures.filter((m) => !!m.field);
    if (!this.barCategory || !filledMeasures.length) return false;
    if (this.isBarComparison) return filledMeasures.length >= 2;
    if (this.barSeriesRequired && !this.barSeries) return false;
    return true;
  }

  private buildBarTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.host.newTileBase(payload);
    base.format = { ...this.host.defaultTileFormat('BAR'), ...this.host.barFormat, title: this.host.title.trim() || undefined };
    const rows = this.applyBarComputedColumns(this.host.cachedRows(this.host.drillDataset) || []);
    return { ...base, barRows: rows, barData: this.buildBarWidgetData(base.id, base.format) };
  }

  private buildBarCapabilities(): BarColumnCapabilities {
    const measures = this.barMeasures
      .filter((measure) => measure.field)
      .map((measure) => ({
        field: measure.field,
        label: measure.label || measure.field,
        renderAs: measure.renderAs,
        axis: measure.axis,
      }));
    const secondaryField = this.barActiveSecondaryField.trim();
    if (secondaryField && !measures.some((m) => m.field === secondaryField)) {
      measures.push({ field: secondaryField, label: secondaryField, renderAs: 'line', axis: 'y2' });
    }
    const referenceLines = this.barReferenceLines
      .filter((line) => line.value !== null && Number.isFinite(Number(line.value)))
      .map((line) => ({ value: Number(line.value), label: line.label || undefined, color: line.color || undefined }));
    return {
      orientation: this.barOrientation,
      variant: this.barVariant,
      category: this.barCategory,
      categoryParent: this.barCategoryParent && this.barCategoryParent !== this.barCategory ? this.barCategoryParent : undefined,
      categoryLabel: this.barCategoryLabel || this.barCategory,
      series: this.barVariantUsesSeries && this.barSeries ? this.barSeries : undefined,
      measures,
      ...(this.barXAxisFontWeight ? { xAxisFontWeight: this.barXAxisFontWeight } : {}),
      ...(this.barYAxisFontWeight ? { yAxisFontWeight: this.barYAxisFontWeight } : {}),
      ...(this.barSecondaryAxisFontWeight ? { axis2FontWeight: this.barSecondaryAxisFontWeight } : {}),
      ...(referenceLines.length ? { referenceLines } : {}),
      ...(this.host.layout.options?.barChartDefaults ? { chartDefaults: this.host.layout.options.barChartDefaults } : {}),
      variantFlags: {
        stack: this.barVariantEntry?.stack === true,
        normalize100: this.barVariantEntry?.normalize100 === true,
        comparison: this.barVariantEntry?.comparison === true,
      },
      ...(this.barComputedColumns.length ? { computedColumns: this.barComputedColumns } : {}),
    } as BarColumnCapabilities;
  }

  private applyBarFormatToCapabilities(capabilities: BarColumnCapabilities, format?: Record<string, unknown>): BarColumnCapabilities {
    return applyBarFormatToCapabilities(capabilities, format, (key) => this.host.resolvePaletteColors(key));
  }

  private applyBarComputeResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): void {
    const componentId = String(payload['componentId'] ?? '');
    const tile = this.host.dashboardTiles.find((candidate) => candidate.componentId === componentId);
    if (!tile) return;
    const rows = extractBarComputeRows(response?.['responseList']) ?? extractBarComputeRows(response);
    if (!rows?.length) {
      this.ui.showError('The value formula returned no data — check the formula and its references.');
      return;
    }
    const widgetData = buildBarComputeWidgetData(response, {
      widgetId: 'dvm-tile-bar-' + tile.id,
      title: this.host.title.trim(),
      capabilities: this.applyBarFormatToCapabilities(this.buildBarCapabilities(), tile.format) as unknown as Record<string, unknown>,
      payload,
      format: tile.format,
    });
    this.host.dashboardTiles = this.host.dashboardTiles.map((candidate) =>
      candidate.id === tile.id
        ? { ...candidate, barRows: rows, barData: widgetData }
        : candidate,
    );
  }

  private buildBarWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.applyBarComputedColumns(this.host.cachedRows(this.host.drillDataset) || []);
    const capabilities = this.applyBarFormatToCapabilities(this.buildBarCapabilities(), format);
    return {
      widgetId: 'dvm-tile-bar-' + tileId,
      title: this.host.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    const filledMeasures = this.barMeasures.filter((measure) => !!measure.field);
    if (!this.barCategory) missing.push('Category');
    if (this.isBarComparison) {
      if (filledMeasures.length < 2) missing.push('Two measures (primary + comparison)');
      return missing;
    }
    if (!filledMeasures.length && !this.barUsesValueFormula) missing.push('At least one measure');
    if (this.barSeriesRequired && !this.barSeries) missing.push('Series (split) column');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const firstBarMeasure = this.barMeasures.find((measure) => !!measure.field);
    const barFormula = this.barValueFormula.trim();
    const barReferences = this.host.referencesForFormula(this.host.componentId, barFormula);
    const barGroupFormulaEntries = this.barGroupFormulas
      .map((entry) => ({ group: entry.group.trim(), formula: entry.formula.trim() }))
      .filter((entry) => entry.group && entry.formula);
    const barGroupFormulas = barGroupFormulaEntries.length
      ? Object.fromEntries(barGroupFormulaEntries.map((entry) => [entry.group, entry.formula]))
      : undefined;
    const barFilters = barGroupFormulaEntries.length && !barFormula
      ? [{ dataset: this.host.drillDataset, field: this.barCategory, operator: 'IN', value: barGroupFormulaEntries.map((entry) => entry.group) }]
      : undefined;
    const barValueFallback = firstBarMeasure?.field ? `SUM(${firstBarMeasure.field})` : '';
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: this.host.selectedLayoutType, // 'BAR'
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      groupBy: {
        field: this.barCategory,
        label: this.barCategoryLabel || this.barCategory,
      },
      value: {
        label: this.barValueLabel.trim() || firstBarMeasure?.label || firstBarMeasure?.field || 'Value',
        formula: barFormula || (barGroupFormulas ? '' : barValueFallback),
        ...(barGroupFormulas ? { groupFormulas: barGroupFormulas } : {}),
        ...(barReferences ? { references: barReferences } : {}),
      },
      capabilities: this.buildBarCapabilities() as unknown as Record<string, unknown>,
      ...(barFilters ? { filters: barFilters } : {}),
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
      ?? ((tile.barData?.['capabilities'] as Record<string, unknown> | undefined))
      ?? {};
    this.barOrientation = (String(caps['orientation'] ?? 'vertical') as BarColumnOrientation);
    this.barVariant = (String(caps['variant'] ?? 'simple') as BarColumnVariant);
    this.barCategory = String(caps['category'] ?? '');
    this.barCategoryParent = String(caps['categoryParent'] ?? '');
    this.barCategoryLabel = String(caps['categoryLabel'] ?? '');
    this.barXAxisFontWeight = String(caps['xAxisFontWeight'] ?? '');
    this.barYAxisFontWeight = String(caps['yAxisFontWeight'] ?? '');
    this.barSecondaryAxisFontWeight = String(caps['axis2FontWeight'] ?? '');
    this.barSeries = String(caps['series'] ?? '');
    const barValue = (payload['value'] as Record<string, unknown> | undefined) ?? {};
    this.barValueLabel = String(barValue['label'] ?? '');
    const savedBarFormula = String(barValue['formula'] ?? '');
    this.barValueFormula = /^SUM\([^()]*\)$/i.test(savedBarFormula.trim()) ? '' : savedBarFormula;
    const barSavedGroupFormulas = (barValue['groupFormulas'] as Record<string, unknown>) || {};
    this.barGroupFormulas = Object.keys(barSavedGroupFormulas)
      .map((group) => ({ group, formula: String(barSavedGroupFormulas[group] ?? '') }));
    this.host.rehydrateOwnFormulaReferences(this.host.componentId, barValue['references']);
    const savedMeasures = Array.isArray(caps['measures']) ? caps['measures'] as Array<Record<string, unknown>> : [];
    this.barMeasures = savedMeasures.length
      ? savedMeasures.map((m) => ({
          id: this.nextBarMeasureId(),
          field: String(m['field'] ?? ''),
          label: String(m['label'] ?? ''),
          renderAs: (String(m['renderAs'] ?? 'bar') as 'bar' | 'line' | 'area'),
          axis: (String(m['axis'] ?? 'y1') as 'y1' | 'y2'),
        }))
      : [];
    this.barSecondaryXField = '';
    this.barSecondaryYField = '';
    const secondaryMeasure = this.barMeasures.find((m) => m.axis === 'y2');
    if (secondaryMeasure) {
      if (this.barOrientation === 'horizontal') this.barSecondaryXField = secondaryMeasure.field;
      else this.barSecondaryYField = secondaryMeasure.field;
    }
    const savedRefLines = Array.isArray(caps['referenceLines']) ? caps['referenceLines'] as Array<Record<string, unknown>> : [];
    this.barReferenceLines = savedRefLines.map((line) => {
      this.barRefLineIdCounter += 1;
      return {
        id: `bar-refline-${this.barRefLineIdCounter}`,
        value: line['value'] == null ? null : Number(line['value']),
        label: String(line['label'] ?? ''),
        color: String(line['color'] ?? '#64748b'),
      };
    });
    const savedComputed = Array.isArray(caps['computedColumns']) ? caps['computedColumns'] as Array<Record<string, unknown>> : [];
    this.barComputedColumns = savedComputed
      .map((c) => ({ name: String(c['name'] ?? ''), expression: String(c['expression'] ?? '') }))
      .filter((c) => c.name && c.expression);
    this.host.barFormat = { ...(tile.format || {}) };
    if (this.host.drillDataset) this.host.loadColumnsForDataset(this.host.drillDataset);
  }

  reset(): void {
    this.barCategory = '';
    this.barCategoryParent = '';
    this.barCategoryLabel = '';
    this.barSeries = '';
    this.barValueFormula = '';
    this.barValueLabel = '';
    this.barGroupFormulas = [];
    this.barSecondaryXField = '';
    this.barSecondaryYField = '';
    this.barXAxisFontWeight = '';
    this.barYAxisFontWeight = '';
    this.barSecondaryAxisFontWeight = '';
    this.barMeasures = [];
    this.barReferenceLines = [];
    this.barComputedColumns = [];
    this.newBarColumnName = '';
    this.newBarColumnExpression = '';
    this.newBarColumnError = '';
  }

  /** A fresh Bar form starts with one empty measure row. */
  onLayoutActivated(): void {
    if (!this.barMeasures.length) this.addBarMeasure();
  }

  onFormatChanged(): void {
    this.refreshEditingBarTile();
  }

  onDatasetRowsLoaded(): void {
    this.refreshEditingBarTile();
  }

  /** Promotes the live preview tile (or adds a new one), then POSTs; a value formula makes the backend rows win. */
  generate(payload: Record<string, unknown>, url: string): boolean {
    this.host.isGenerating = false;
    if (this.host.previewTileId && this.host.dashboardTiles.some((t) => t.id === this.host.previewTileId)) {
      const id = this.host.previewTileId;
      this.host.dashboardTiles = this.host.dashboardTiles.map((t) =>
        t.id === id ? { ...this.rebuildBarTile(t), payload } : t,
      );
      this.host.previewTileId = null; // promoted → no longer a draft
    } else {
      const fresh = this.buildBarTile(payload);
      const editing = this.host.editingTileId ? this.host.dashboardTiles.find((t) => t.id === this.host.editingTileId) : undefined;
      if (editing?.barData && isServerComputedClientTile(editing.barData as Record<string, unknown>, this.barCategory)) {
        fresh.barData = { ...(editing.barData as Record<string, unknown>), title: this.host.title.trim() || fresh.title };
        fresh.barRows = editing.barRows ?? [];
      }
      this.host.addTile(fresh);
    }
    (this.host.requestGenerate?.(url, payload) ?? this.http.post<Record<string, unknown>>(url, payload)).subscribe({
      next: (response) => {
        const serverRows = extractBarComputeRows(response?.['responseList']) ?? extractBarComputeRows(response);
        if (this.barUsesValueFormula || serverRows?.length) this.applyBarComputeResponse(response, payload);
        this.ui.showSuccess('Bar chart added to the dashboard.');
      },
      error: () => this.ui.showError('Bar chart added, but saving its configuration failed.'),
    });
    return true;
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.barData) return;
    const capabilities = this.applyBarFormatToCapabilities({ ...(tile.barData['capabilities'] as unknown as BarColumnCapabilities) }, format);
    tile.barData = {
      ...tile.barData,
      title: (format['title'] as string) || tile.title,
      capabilities: capabilities as unknown as Record<string, unknown>,
    };
  }

  /** A server-computed envelope replaces the widget data; plain rows only when they fit the tile's category field. */
  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    const envRows = extractBarComputeRows(result['responseList']) ?? extractBarComputeRows(result);
    if (envRows?.length && tile.barData) {
      tile.barData = buildBarComputeWidgetData(result, {
        widgetId: 'dvm-tile-bar-' + tile.id,
        title: tile.title,
        capabilities: (tile.barData['capabilities'] as Record<string, unknown> | undefined) ?? {},
        payload: (tile.payload as Record<string, unknown>) ?? {},
        format: tile.format,
      });
      tile.barRows = envRows;
      return true;
    }
    const rows = (Array.isArray(result['data'])
      ? result['data']
      : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
    if (tile.barData && this.host.rowsFitClientChart(rows, tile.barData, 'category')) {
      tile.barData = { ...tile.barData, staticData: Array.isArray(rows) ? rows : [] };
      return true;
    }
    return false;
  }
}
